/**
 * Chat Bot Settings — /settings/chat-bot  (Iter 90)
 *
 * Admin/manager UI to configure Rigo — bot name, greeting, office hours,
 * off-hours message, escalation message, fallback, retention, and the
 * keyword→reply triggers list.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  Robot, Plus, Trash, FloppyDisk, Clock, ChatCircleDots, Warning, Globe,
  ToggleLeft, ToggleRight, BookOpen, ShieldWarning, MagnifyingGlass, Prohibit,
  UploadSimple, PencilSimple, File as FileIcon, ArrowClockwise,
} from "@phosphor-icons/react";
import { RigoAvatar } from "../../components/rigo";
import CustomerTranslationToggle from "../../components/crm/CustomerTranslationToggle";

export default function ChatBotSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  // Iter 140 — knowledge base, blocklist, safety events
  const [kb, setKb] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [events, setEvents] = useState([]);
  const [kbForm, setKbForm] = useState({ title: "", body: "", tags: "" });
  const [termInput, setTermInput] = useState("");
  // Iter 148 — master knowledge document
  const [master, setMaster] = useState(null);       // {content, title, updated_at, ...}
  const [masterDraft, setMasterDraft] = useState("");
  const [masterTitle, setMasterTitle] = useState("");
  const [masterBusy, setMasterBusy] = useState(false);
  const [masterEdit, setMasterEdit] = useState(false);

  const load = async () => {
    const r = await api.get("/chat-bot/settings");
    setCfg(r.data);
  };
  const loadKb = async () => {
    try { const r = await api.get("/chat-ai/knowledge"); setKb(r.data || []); }
    catch { /* silent */ }
  };
  const loadBlocked = async () => {
    try { const r = await api.get("/chat-ai/blocked-terms"); setBlocked(r.data || []); }
    catch { /* silent */ }
  };
  const loadEvents = async () => {
    try { const r = await api.get("/chat-ai/safety-events?limit=10"); setEvents(r.data || []); }
    catch { /* silent */ }
  };
  const loadMaster = async () => {
    try {
      const r = await api.get("/chat-ai/master-kb");
      setMaster(r.data);
      setMasterDraft(r.data?.content || "");
      setMasterTitle(r.data?.title || "");
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadKb(); loadBlocked(); loadEvents(); loadMaster(); }, []);

  if (!cfg) return <div className="p-8 text-sm text-[#8A8780]">Loading Rigo settings…</div>;

  const set = (patch) => setCfg(c => ({ ...c, ...patch }));

  // ── Iter 148 · Master KB actions ──
  const uploadMaster = async (file) => {
    if (!file) return;
    setMasterBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (masterTitle.trim()) fd.append("title", masterTitle.trim());
      await api.post("/chat-ai/master-kb/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${file.name}`);
      await loadMaster();
      setMasterEdit(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMasterBusy(false); }
  };
  const saveMaster = async () => {
    setMasterBusy(true);
    try {
      await api.put("/chat-ai/master-kb",
        { content: masterDraft, title: masterTitle.trim() || null });
      toast.success("Knowledge saved");
      await loadMaster();
      setMasterEdit(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMasterBusy(false); }
  };
  const clearMaster = async () => {
    if (!confirm("Delete the master knowledge document?")) return;
    setMasterBusy(true);
    try {
      await api.delete("/chat-ai/master-kb");
      toast.success("Master knowledge cleared");
      setMasterDraft(""); setMasterTitle("");
      await loadMaster();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMasterBusy(false); }
  };

  const addKb = async () => {
    const title = (kbForm.title || "").trim();
    const body = (kbForm.body || "").trim();
    if (!title || !body) { toast.error("Title and body required"); return; }
    try {
      const tags = kbForm.tags
        ? kbForm.tags.split(",").map(t => t.trim()).filter(Boolean)
        : [];
      await api.post("/chat-ai/knowledge", { title, body, tags });
      setKbForm({ title: "", body: "", tags: "" });
      toast.success("Knowledge entry added");
      loadKb();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleKb = async (row) => {
    try {
      await api.put(`/chat-ai/knowledge/${row.id}`, {
        title: row.title, body: row.body, tags: row.tags || [],
        active: !row.active,
      });
      loadKb();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteKb = async (id) => {
    if (!confirm("Delete this knowledge entry?")) return;
    try { await api.delete(`/chat-ai/knowledge/${id}`); loadKb(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const addTerm = async () => {
    const t = termInput.trim();
    if (!t) return;
    try {
      await api.post("/chat-ai/blocked-terms", { term: t });
      setTermInput(""); loadBlocked();
      toast.success("Sensitive term added");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteTerm = async (id) => {
    try { await api.delete(`/chat-ai/blocked-terms/${id}`); loadBlocked(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addTrigger = () => set({
    triggers: [...(cfg.triggers || []), { keywords: [], reply: "" }],
  });
  const removeTrigger = (i) => set({
    triggers: cfg.triggers.filter((_, ix) => ix !== i),
  });
  const updTrigger = (i, patch) => set({
    triggers: cfg.triggers.map((t, ix) => ix === i ? { ...t, ...patch } : t),
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/chat-bot/settings", {
        bot_name: cfg.bot_name, greeting: cfg.greeting,
        office_start: cfg.office_start, office_end: cfg.office_end,
        office_tz_offset_min: cfg.office_tz_offset_min,
        off_hours_message: cfg.off_hours_message,
        escalation_message: cfg.escalation_message,
        fallback: cfg.fallback,
        retention_days: Number(cfg.retention_days),
        retention_notice: cfg.retention_notice,
        triggers: cfg.triggers,
        // Iter 92 — public site guest chat
        public_chat_enabled: !!cfg.public_chat_enabled,
        public_greeting: cfg.public_greeting,
        public_register_url: cfg.public_register_url,
        public_register_cta: cfg.public_register_cta,
        // Iter 139 — Chat AI auto-reply
        auto_ai_off_hours_enabled: !!cfg.auto_ai_off_hours_enabled,
        auto_ai_off_hours_start: cfg.auto_ai_off_hours_start,
        auto_ai_off_hours_end: cfg.auto_ai_off_hours_end,
        auto_ai_off_hours_tz_offset_min: Number(cfg.auto_ai_off_hours_tz_offset_min ?? 330),
        auto_ai_show_as_ai: !!cfg.auto_ai_show_as_ai,
        // Iter 140 — investigate mode + registered gate
        auto_ai_investigate_mode: !!cfg.auto_ai_investigate_mode,
        auto_ai_registered_only: cfg.auto_ai_registered_only !== false,
      });
      toast.success("Rigo settings saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6" data-testid="chatbot-settings">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">CRM · Automations</div>
        <div className="flex items-center gap-3 mt-1">
          <RigoAvatar size={48}/>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl">
              {cfg.bot_name || "Rigo"} · Chat bot
            </h1>
            <p className="text-sm text-[#5C5A55] max-w-2xl">
              Configure keyword triggers, office hours and auto-away messages. Rigo greets
              every customer, handles common questions, and hands off to the office team
              when someone types &ldquo;human&rdquo; or during office hours.
            </p>
          </div>
        </div>
      </div>

      <CustomerTranslationToggle />

      {/* Identity */}
      <Card>
        <SectionHead icon={ChatCircleDots} title="Identity" sub="Bot name + greeting shown to customers"/>
        <Field label="Bot name">
          <input value={cfg.bot_name || ""} onChange={e => set({ bot_name: e.target.value })}
                 data-testid="cb-bot-name"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        </Field>
        <Field label="Greeting (first bot message)">
          <textarea rows={3} value={cfg.greeting || ""}
                    onChange={e => set({ greeting: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
      </Card>

      {/* Office hours */}
      <Card>
        <SectionHead icon={Clock} title="Office hours" sub="Customers messaging outside these hours get the auto-away reply"/>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Start (HH:MM)">
            <input value={cfg.office_start || ""} onChange={e => set({ office_start: e.target.value })}
                   data-testid="cb-office-start"
                   placeholder="10:00"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
          <Field label="End (HH:MM)">
            <input value={cfg.office_end || ""} onChange={e => set({ office_end: e.target.value })}
                   data-testid="cb-office-end"
                   placeholder="17:00"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
          <Field label="Timezone (minutes offset from UTC)">
            <input type="number" value={cfg.office_tz_offset_min ?? 330}
                   onChange={e => set({ office_tz_offset_min: Number(e.target.value) })}
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
            <div className="text-[10px] text-[#8A8780] mt-0.5">IST = 330 (+5:30)</div>
          </Field>
        </div>
        <Field label="Auto-away message (outside office hours)">
          <textarea rows={3} value={cfg.off_hours_message || ""}
                    onChange={e => set({ off_hours_message: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
        <Field label="Escalation message (when customer types 'human')">
          <textarea rows={2} value={cfg.escalation_message || ""}
                    onChange={e => set({ escalation_message: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
      </Card>

      {/* Triggers */}
      <Card>
        <SectionHead icon={Robot} title="Bot triggers" sub="Case-insensitive keyword match. First matching trigger wins."/>
        <div className="space-y-3">
          {(cfg.triggers || []).map((t, i) => (
            <div key={i} className="border border-[#E5E2D9] rounded-md p-3 bg-[#FAFAF7]"
                 data-testid={`cb-trigger-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780]">
                  Trigger #{i + 1}
                </div>
                <button onClick={() => removeTrigger(i)} data-testid={`cb-trigger-del-${i}`}
                        className="text-[#7A2E18] text-xs inline-flex items-center gap-1">
                  <Trash size={12}/>Remove
                </button>
              </div>
              <input value={(t.keywords || []).join(", ")}
                     onChange={e => updTrigger(i, { keywords: e.target.value.split(",").map(k => k.trim()).filter(Boolean) })}
                     placeholder="Keywords, comma-separated (e.g. pickup, collection)"
                     className="w-full h-9 px-3 mb-2 border border-[#E5E2D9] rounded-md text-sm"/>
              <textarea rows={2} value={t.reply || ""}
                        onChange={e => updTrigger(i, { reply: e.target.value })}
                        placeholder="Reply text — use __ESCALATE__ to hand off to office team"
                        className="w-full p-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
          ))}
          <button onClick={addTrigger} data-testid="cb-trigger-add"
                  className="w-full h-10 border border-dashed border-[#2D5A27] text-[#2D5A27] rounded-md text-sm inline-flex items-center justify-center gap-1 hover:bg-[#F0F7EE]">
            <Plus size={13} weight="bold"/>Add trigger
          </button>
        </div>
        <Field label="Fallback reply (when no trigger matches)">
          <textarea rows={2} value={cfg.fallback || ""}
                    onChange={e => set({ fallback: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
      </Card>

      {/* Iter 92 — Public marketing-site chat toggle (super-admin only) */}
      <Card>
        <SectionHead icon={Globe} title="Public website chat"
          sub="Show a Rigo chat bubble on the marketing site for guest visitors (rescigo.in home & landing pages)."/>
        <button type="button"
                onClick={() => set({ public_chat_enabled: !cfg.public_chat_enabled })}
                data-testid="cb-public-toggle"
                className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border-2 transition-colors ${
                  cfg.public_chat_enabled
                    ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]"
                    : "bg-[#EEEBE2] border-[#B7B3A6] text-[#5C5A55]"
                }`}>
          {cfg.public_chat_enabled
            ? <><ToggleRight size={18} weight="fill" color="#2D5A27"/>Guest chat ON</>
            : <><ToggleLeft size={18} weight="fill"/>Guest chat OFF</>}
        </button>
        <Field label="Public greeting (first bot message to guest visitors)">
          <textarea rows={3} value={cfg.public_greeting || ""}
                    onChange={e => set({ public_greeting: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Register link URL">
            <input value={cfg.public_register_url || ""}
                   onChange={e => set({ public_register_url: e.target.value })}
                   placeholder="/register"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
          <Field label="Register CTA button label">
            <input value={cfg.public_register_cta || ""}
                   onChange={e => set({ public_register_cta: e.target.value })}
                   placeholder="Register on Rescigo"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          </Field>
        </div>
        <div className="text-[11px] text-[#5C5A55] bg-[#F7F6F2] rounded p-2 mt-1">
          The public widget uses the same trigger table below, but it&apos;s <strong>stateless</strong> —
          nothing is stored in the database. When Rigo can&apos;t answer or the visitor types
          &ldquo;human&rdquo;, the widget shows the register button that links to the URL above.
        </div>
      </Card>

      {/* Iter 139 — Auto-AI chat replies */}
      <Card>
        <SectionHead icon={Robot} title="AI auto-reply (Rigo AI)"
          sub="When enabled, Gemini drafts an English reply on behalf of your team. Use the composer's ✨ button any time; turn on the after-hours window to have AI answer while your team sleeps."/>

        {/* Iter 140 — master switches: investigate mode + registered gate */}
        <div className="flex flex-wrap gap-3 items-center">
          <button type="button"
                  onClick={() => set({ auto_ai_investigate_mode: !cfg.auto_ai_investigate_mode })}
                  data-testid="cb-auto-ai-investigate-toggle"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border-2 transition-colors ${
                    cfg.auto_ai_investigate_mode
                      ? "bg-[#DDE7F1] border-[#0B2B66] text-[#0B2B66]"
                      : "bg-[#EEEBE2] border-[#B7B3A6] text-[#5C5A55]"
                  }`}>
            {cfg.auto_ai_investigate_mode
              ? <><ToggleRight size={18} weight="fill" color="#0B2B66"/>Investigate + auto reply ON (Rigo bot muted)</>
              : <><ToggleLeft size={18} weight="fill"/>Investigate + auto reply OFF</>}
          </button>
          <button type="button"
                  onClick={() => set({ auto_ai_registered_only: !cfg.auto_ai_registered_only })}
                  data-testid="cb-auto-ai-registered-toggle"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border-2 transition-colors ${
                    cfg.auto_ai_registered_only
                      ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]"
                      : "bg-[#EEEBE2] border-[#B7B3A6] text-[#5C5A55]"
                  }`}>
            {cfg.auto_ai_registered_only
              ? <><ToggleRight size={18} weight="fill" color="#2D5A27"/>Only registered customers</>
              : <><ToggleLeft size={18} weight="fill"/>All chatters (incl. guests)</>}
          </button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button type="button"
                  onClick={() => set({ auto_ai_off_hours_enabled: !cfg.auto_ai_off_hours_enabled })}
                  data-testid="cb-auto-ai-off-hours-toggle"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border-2 transition-colors ${
                    cfg.auto_ai_off_hours_enabled
                      ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]"
                      : "bg-[#EEEBE2] border-[#B7B3A6] text-[#5C5A55]"
                  }`}>
            {cfg.auto_ai_off_hours_enabled
              ? <><ToggleRight size={18} weight="fill" color="#2D5A27"/>After-hours auto AI ON</>
              : <><ToggleLeft size={18} weight="fill"/>After-hours auto AI OFF</>}
          </button>
          <button type="button"
                  onClick={() => set({ auto_ai_show_as_ai: !cfg.auto_ai_show_as_ai })}
                  data-testid="cb-auto-ai-show-toggle"
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-medium border-2 transition-colors ${
                    cfg.auto_ai_show_as_ai
                      ? "bg-[#DDE7F1] border-[#0B2B66] text-[#0B2B66]"
                      : "bg-[#EEEBE2] border-[#B7B3A6] text-[#5C5A55]"
                  }`}>
            {cfg.auto_ai_show_as_ai
              ? <><ToggleRight size={18} weight="fill" color="#0B2B66"/>Show as &quot;Rigo AI&quot;</>
              : <><ToggleLeft size={18} weight="fill"/>Silent (appears as team reply)</>}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <Field label="After-hours start (24h)">
            <input type="time" value={cfg.auto_ai_off_hours_start || "18:00"}
                   onChange={e => set({ auto_ai_off_hours_start: e.target.value })}
                   data-testid="cb-auto-ai-start"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
          <Field label="After-hours end (24h)">
            <input type="time" value={cfg.auto_ai_off_hours_end || "09:00"}
                   onChange={e => set({ auto_ai_off_hours_end: e.target.value })}
                   data-testid="cb-auto-ai-end"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
          <Field label="Timezone offset (minutes)">
            <input type="number" step="15"
                   value={cfg.auto_ai_off_hours_tz_offset_min ?? 330}
                   onChange={e => set({ auto_ai_off_hours_tz_offset_min: e.target.value })}
                   data-testid="cb-auto-ai-tz"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
        </div>
        <div className="text-[11px] text-[#5C5A55] bg-[#F7F6F2] rounded p-2 mt-1">
          Default window is <strong>18:00 → 09:00 IST</strong> (offset 330). Time is evaluated in the tenant&apos;s
          timezone (offset in minutes east of UTC). Per-conversation Auto-AI toggle in the CRM chat header
          always wins over this window.
        </div>
      </Card>

      {/* Iter 148 — Master knowledge document */}
      <Card>
        <SectionHead icon={FileIcon} title="Master knowledge document"
          sub="Upload one big document (conversation flow, tone rules, all FAQs) or paste text below. Rigo will follow this on every reply."/>
        {master && (
          <div className="rounded-md border border-[#E5E2D9] bg-[#F7F6F2] p-3 mb-3 text-xs"
               data-testid="cb-master-meta">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium text-[#0F1F0C]">
                {master.title || "Untitled"}
              </span>
              <span className="text-[#5C5A55]">
                {master.size_chars?.toLocaleString?.() || 0} / {master.max_chars?.toLocaleString?.() || 60000} chars
              </span>
              {master.source_file && (
                <span className="text-[#5C5A55] inline-flex items-center gap-1">
                  <FileIcon size={11}/>{master.source_file}
                </span>
              )}
              {master.updated_at && (
                <span className="text-[#8A8780]">
                  updated {new Date(master.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"})}
                  {master.updated_by_name && ` · by ${master.updated_by_name}`}
                </span>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cb-master-actions">
          <label className="h-9 px-3 rounded-md border border-[#2D5A27] bg-[#2D5A27] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#3A7033] cursor-pointer">
            <UploadSimple size={13} weight="bold"/>
            {masterBusy ? "Uploading…" : "Upload .docx / .pdf / .txt"}
            <input type="file" className="hidden"
                    accept=".docx,.pdf,.txt,.md"
                    disabled={masterBusy}
                    data-testid="cb-master-upload"
                    onChange={(e) => uploadMaster(e.target.files?.[0])}/>
          </label>
          <button onClick={() => setMasterEdit((v) => !v)}
                   data-testid="cb-master-toggle-edit"
                   className="h-9 px-3 rounded-md border border-[#E5E2D9] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <PencilSimple size={13}/>{masterEdit ? "Hide editor" : "Edit text"}
          </button>
          <button onClick={loadMaster}
                   data-testid="cb-master-refresh"
                   className="h-9 px-3 rounded-md border border-[#E5E2D9] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <ArrowClockwise size={13}/>Refresh
          </button>
          {master?.content && (
            <button onClick={clearMaster}
                     data-testid="cb-master-clear"
                     className="h-9 px-3 rounded-md border border-[#E5E2D9] text-[#7A2E18] text-xs inline-flex items-center gap-1.5 hover:bg-[#F7EFEC] ml-auto">
              <Trash size={13}/>Clear
            </button>
          )}
        </div>
        {masterEdit && (
          <div className="space-y-2" data-testid="cb-master-editor">
            <input value={masterTitle}
                    onChange={(e) => setMasterTitle(e.target.value)}
                    placeholder="Title (e.g. Rescigo Conversation Flow v2)"
                    data-testid="cb-master-title-input"
                    className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-sm focus:outline-none focus:border-[#2D5A27]"/>
            <textarea value={masterDraft}
                       onChange={(e) => setMasterDraft(e.target.value)}
                       rows={16}
                       data-testid="cb-master-textarea"
                       placeholder="Paste or type the full knowledge document — conversation flow, tone rules, FAQ answers…"
                       className="w-full p-3 border border-[#E5E2D9] rounded-md text-xs font-mono leading-relaxed focus:outline-none focus:border-[#2D5A27]"/>
            <div className="flex items-center justify-between text-[11px] text-[#8A8780]">
              <span>{masterDraft.length.toLocaleString()} / {(master?.max_chars || 60000).toLocaleString()} characters</span>
              <div className="flex gap-2">
                <button onClick={() => { setMasterDraft(master?.content || ""); setMasterEdit(false); }}
                         data-testid="cb-master-cancel"
                         className="h-8 px-3 rounded-md border border-[#E5E2D9] text-xs hover:bg-[#F7F6F2]">
                  Cancel
                </button>
                <button onClick={saveMaster} disabled={masterBusy}
                         data-testid="cb-master-save"
                         className="h-8 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-medium inline-flex items-center gap-1.5">
                  <FloppyDisk size={12} weight="bold"/>
                  {masterBusy ? "Saving…" : "Save knowledge"}
                </button>
              </div>
            </div>
          </div>
        )}
        {!masterEdit && master?.content && (
          <div className="border border-[#E5E2D9] rounded-md bg-white p-3 text-xs font-mono leading-relaxed max-h-72 overflow-auto whitespace-pre-wrap text-[#0F1F0C]"
               data-testid="cb-master-preview">
            {master.content}
          </div>
        )}
        {!masterEdit && !master?.content && (
          <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center text-xs text-[#8A8780]"
               data-testid="cb-master-empty">
            No master knowledge yet — upload a document or click <b>Edit text</b> to start typing.
          </div>
        )}
      </Card>

      {/* Iter 140 — Knowledge base */}
      <Card>
        <SectionHead icon={BookOpen} title="AI knowledge base"
          sub={`Facts you feed the AI so it can answer accurately. Currently ${kb.length}/200 entries. Toggle off any entry to temporarily exclude it.`}/>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cb-kb-list">
          {kb.map(row => (
            <div key={row.id} className="border border-[#E5E2D9] rounded-md p-3 text-xs bg-white"
                 data-testid={`cb-kb-row-${row.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${row.active ? "" : "opacity-50 line-through"}`}>
                    {row.title}
                  </div>
                  <div className="text-[#5C5A55] mt-0.5 line-clamp-3">{row.body}</div>
                  {row.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {row.tags.map(t => (
                        <span key={t} className="px-1.5 py-0.5 rounded bg-[#EFEAD8] text-[#5C4A1E] text-[10px]">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => toggleKb(row)} data-testid={`cb-kb-toggle-${row.id}`}
                          className="p-1 rounded hover:bg-[#F7F6F2]">
                    {row.active
                      ? <ToggleRight size={16} weight="fill" color="#2D5A27"/>
                      : <ToggleLeft size={16} weight="fill" color="#8A8780"/>}
                  </button>
                  <button onClick={() => deleteKb(row.id)} data-testid={`cb-kb-delete-${row.id}`}
                          className="p-1 rounded hover:bg-[#F9EDE7] text-[#C25D38]">
                    <Trash size={13}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
          {kb.length === 0 && (
            <div className="col-span-full text-[11px] text-[#8A8780] italic p-4 text-center bg-[#F7F6F2] rounded">
              No knowledge entries yet — add the first one below.
            </div>
          )}
        </div>
        <div className="border-t border-[#E5E2D9] pt-3 mt-2 space-y-2">
          <input value={kbForm.title} onChange={e => setKbForm({ ...kbForm, title: e.target.value })}
                 placeholder="Title (e.g. 'Bag delivery times')"
                 data-testid="cb-kb-title"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <textarea value={kbForm.body} onChange={e => setKbForm({ ...kbForm, body: e.target.value })}
                     rows={3} placeholder="Facts the AI may cite verbatim…"
                     data-testid="cb-kb-body"
                     className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
          <div className="flex gap-2">
            <input value={kbForm.tags} onChange={e => setKbForm({ ...kbForm, tags: e.target.value })}
                   placeholder="tags, comma, separated"
                   data-testid="cb-kb-tags"
                   className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            <button onClick={addKb} data-testid="cb-kb-add"
                    className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
              <Plus size={12} weight="bold"/>Add
            </button>
          </div>
        </div>
      </Card>

      {/* Iter 140 — Sensitive-term blocklist */}
      <Card>
        <SectionHead icon={Prohibit} title="Sensitive-word / phrase blocklist"
          sub="If a customer chat contains any of these terms, Rigo AI STOPS replying, the conversation is flagged for senior staff, and the customer is marked inactive pending review. Add things like admin passwords, other customers' names, internal codenames, or any words the AI must never respond to."/>
        <div className="flex gap-2">
          <input value={termInput} onChange={e => setTermInput(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && addTerm()}
                 placeholder="e.g. admin_password_123 or 'internal-only'"
                 data-testid="cb-block-input"
                 className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button onClick={addTerm} data-testid="cb-block-add"
                  className="h-10 px-4 border border-[#C25D38] text-[#C25D38] rounded-md text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9EDE7]">
            <Plus size={12} weight="bold"/>Block
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="cb-block-list">
          {blocked.map(row => (
            <span key={row.id}
                  data-testid={`cb-block-row-${row.id}`}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#F4C7B9] text-[#7A2E18] text-[11px]">
              <ShieldWarning size={10}/>{row.term}
              <button onClick={() => deleteTerm(row.id)}
                      data-testid={`cb-block-remove-${row.id}`}
                      className="ml-0.5 p-0.5 hover:bg-[#EEB09B] rounded-full">
                <Trash size={10}/>
              </button>
            </span>
          ))}
          {blocked.length === 0 && (
            <div className="text-[11px] text-[#8A8780] italic">
              No custom terms yet. The AI also blocks emails, OTPs, and long numeric sequences by default.
            </div>
          )}
        </div>
      </Card>

      {/* Iter 140 — Recent safety events */}
      <Card>
        <SectionHead icon={ShieldWarning} title="Recent safety events"
          sub="Last 10 chats where Rigo AI stopped replying because of a security trigger. Investigate + follow up."/>
        <div className="space-y-1.5" data-testid="cb-events-list">
          {events.map(ev => (
            <div key={ev.created_at + (ev.conversation_id || "")}
                 className="flex items-center gap-2 text-[11px] py-1.5 border-b border-[#F0EDE4] last:border-b-0">
              <span className="uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#F4C7B9] text-[#7A2E18] text-[9px] font-semibold">
                {ev.reason}
              </span>
              <span className="flex-1 truncate text-[#3A3A38]">
                {ev.matched || "—"}
              </span>
              <span className="font-mono text-[#8A8780]">
                {(ev.created_at || "").slice(0, 16).replace("T", " ")}
              </span>
            </div>
          ))}
          {events.length === 0 && (
            <div className="text-[11px] text-[#8A8780] italic py-2">
              No safety events yet — great signal that your setup is healthy.
            </div>
          )}
        </div>
      </Card>

      {/* Retention */}
      <Card>
        <SectionHead icon={Warning} title="Retention" sub="Mongo TTL auto-purges messages after this many days"/>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Days">
            <input type="number" min="1" max="365"
                   value={cfg.retention_days || 30}
                   onChange={e => set({ retention_days: e.target.value })}
                   data-testid="cb-retention-days"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
          </Field>
        </div>
        <Field label="Retention notice shown to customer on chat open">
          <textarea rows={2} value={cfg.retention_notice || ""}
                    onChange={e => set({ retention_notice: e.target.value })}
                    className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </Field>
      </Card>

      {/* Sticky save */}
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={busy}
                data-testid="cb-save"
                className="h-11 px-6 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2 shadow-lg">
          <FloppyDisk size={14} weight="fill"/>{busy ? "Saving…" : "Save Rigo settings"}
        </button>
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white border border-[#E5E2D9] rounded-lg p-5 space-y-3">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">{label}</label>
      {children}
    </div>
  );
}
function SectionHead({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} weight="fill" color="#2D5A27"/>
      <div>
        <div className="font-heading text-base leading-tight">{title}</div>
        <div className="text-[11px] text-[#8A8780]">{sub}</div>
      </div>
    </div>
  );
}
